/* eslint-disable */
/**
 * POS Certificates - Property-filtered view of certifications
 * Copy of Certification.jsx filtered to only show POS customers (where property_id is not null)
 */

import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { usePOS } from '../../../contexts/POSContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';
import ErrorDisplay from '../../../components/ErrorDisplay';
import DataTable, { StatusBadge } from '../../../components/DataTable';
import SearchSelect from '../../../components/SearchSelect';

// Status configuration
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'var(--color-text-light)', bg: 'var(--color-bg)' },
  returned: { label: 'Returned', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  pending_verification: { label: 'Pending Verification', color: 'var(--color-info)', bg: 'var(--color-info-bg)' },
  pending_authorization: { label: 'Pending Authorization', color: '#8b5cf6', bg: '#ede9fe' },
  pending_endorsement: { label: 'Pending Endorsement', color: '#a855f7', bg: '#f3e8ff' },
  certified: { label: 'Certified', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  endorsed: { label: 'Endorsed', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  rejected: { label: 'Rejected', color: 'var(--color-error)', bg: 'var(--color-error-bg)' }
};

// Workflow columns for Kanban board
const WORKFLOW_COLUMNS = [
  { id: 'draft', label: 'Draft' },
  { id: 'returned', label: 'Returned' },
  { id: 'pending_verification', label: 'Verification' },
  { id: 'pending_authorization', label: 'Authorization' },
  { id: 'certified', label: 'Certified' }
];

function POSCertificates() {
  const { supabase, user, hasPermission, isSuperAdmin, getScope } = useAuth();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('pos_reports');

  // POSContext for persistent property selection
  const {
    selectedPropertyId,
    setSelectedPropertyId,
    userProperties,
    propertiesLoading
  } = usePOS();

  // Permissions
  const canVerify = hasPermission('certification', 'verify');
  // Authorise is restricted to super-admins and country admins (global scope)
  // and never to the same person who verified the submission (per-row check below).
  const canAuthorize = isSuperAdmin || getScope() === 'global';
  const canAuthorizeRow = (verifiedBy: string | null | undefined) =>
    canAuthorize && verifiedBy !== user?.id;

  // Data state
  const [submissions, setSubmissions] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: string } | null>(null);

  const showToast = useCallback((message: string, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Admin users who can verify/authorize
  const [adminUsers, setAdminUsers] = useState<{ id: string; name: string; canVerify: boolean; canAuthorize: boolean }[]>([]);

  // View mode: 'list' or 'card' - saved to localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('posCertViewMode') || 'list';
  });

  // Modal state
  const [viewingSubmission, setViewingSubmission] = useState<Record<string, any> | null>(null);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('posCertViewMode', viewMode);
  }, [viewMode]);

  // Load admin users who can verify/authorize
  useEffect(() => {
    async function loadAdminUsers() {
      try {
        // Get users with certification permissions or super_admin
        const { data, error: err } = await supabase
          .from('users')
          .select('id, first_name, last_name, permissions')
          .eq('status', 'active')
          .order('first_name');

        if (err) throw err;

        // Filter to users who can verify OR authorize
        const eligible = (data || []).filter((u: Record<string, any>) => {
          const perms = u.permissions || {};
          return perms.admin?.super_admin === true ||
                 perms.certification?.verify === true ||
                 perms.certification?.authorize === true;
        }).map((u: Record<string, any>) => ({
          id: u.id,
          name: `${u.first_name} ${u.last_name}`.trim(),
          canVerify: u.permissions?.admin?.super_admin === true || u.permissions?.certification?.verify === true,
          canAuthorize: u.permissions?.admin?.super_admin === true || u.permissions?.certification?.authorize === true
        }));

        setAdminUsers(eligible);
      } catch (_err) {
        console.error('Error loading admin users:', _err);
      }
    }

    loadAdminUsers();
  }, [supabase]);

  // Load submissions when property changes
  useEffect(() => {
    if (selectedPropertyId) {
      loadSubmissions();
    } else {
      setSubmissions([]);
      setLoading(false);
    }
  }, [selectedPropertyId]);

  async function loadSubmissions() {
    if (!selectedPropertyId) return;

    try {
      setLoading(true);
      setError(null);

      // Get submissions for the selected property
      const { data, error: err } = await supabase
        .from('cert_submissions')
        .select(`
          id,
          service_date,
          operator_id,
          service_location,
          submission_status,
          created_at,
          invoice_number,
          customer_name,
          property_id
        `)
        .eq('property_id', selectedPropertyId)
        .order('created_at', { ascending: false })
        .limit(10000);

      if (err) throw err;

      // Get operator names
      const operatorIds = [...new Set((data || []).map(s => s.operator_id).filter(Boolean))];
      const operatorMap: Record<string, string> = {};
      if (operatorIds.length > 0) {
        const { data: operators } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .in('id', operatorIds);
        (operators || []).forEach((op: Record<string, any>) => {
          operatorMap[op.id] = `${op.first_name} ${op.last_name}`.trim();
        });
      }

      // Get property names
      const propertyIds = [...new Set((data || []).map((s: Record<string, any>) => s.property_id).filter(Boolean))];
      const propertyMap: Record<string, string> = {};
      if (propertyIds.length > 0) {
        const { data: props } = await supabase
          .from('hosp_properties')
          .select('id, property_name')
          .in('id', propertyIds);
        (props || []).forEach((p: Record<string, any>) => {
          propertyMap[p.id] = p.property_name;
        });
      }

      const enriched = (data || []).map((sub: Record<string, any>) => ({
        ...sub,
        operator_name: operatorMap[sub.operator_id] || 'Unknown',
        property_name: propertyMap[sub.property_id] || 'Unknown',
        display_customer: sub.customer_name || sub.service_location?.split(' - ')[0] || '—'
      }));

      setSubmissions(enriched);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Extract unique operators for filter dropdown
  const operatorOptions = useMemo(() => {
    const operators = new Map();
    submissions.forEach(sub => {
      if (sub.operator_name && sub.operator_name !== 'Unknown') {
        operators.set(sub.operator_name, sub.operator_name);
      }
    });
    return Array.from(operators.values()).sort().map(name => ({
      value: name,
      label: name
    }));
  }, [submissions]);

  // Property filter options
  const propertyOptions = useMemo(() => {
    const props = new Map();
    submissions.forEach(sub => {
      if (sub.property_id && sub.property_name) {
        props.set(sub.property_name, sub.property_name);
      }
    });
    return Array.from(props.values()).sort().map(name => ({
      value: name,
      label: name
    }));
  }, [submissions]);

  // Status filter options
  const statusOptions = useMemo(() => {
    return Object.entries(STATUS_CONFIG).map(([key, config]) => ({
      value: key,
      label: config.label
    }));
  }, []);

  // Group submissions by status for Kanban board
  const submissionsByStatus = useMemo(() => {
    const grouped: Record<string, Record<string, any>[]> = {};
    WORKFLOW_COLUMNS.forEach(col => {
      grouped[col.id] = [];
    });
    submissions.forEach(sub => {
      const bucket = grouped[sub.submission_status];
      if (bucket) {
        bucket.push(sub);
      }
    });
    return grouped;
  }, [submissions]);

  // View submission details
  async function viewSubmission(id: string) {
    setViewingSubmission({ id, loading: true });

    try {
      const { data, error: err } = await supabase
        .from('cert_submissions')
        .select('*')
        .eq('id', id)
        .single();

      if (err) throw err;

      // Get operator name
      let operatorName = 'Unknown';
      if (data.operator_id) {
        const { data: op } = await supabase
          .from('users')
          .select('first_name, last_name')
          .eq('id', data.operator_id)
          .single();
        if (op) operatorName = `${op.first_name} ${op.last_name}`.trim();
      }

      // Get verifier, authorizer, endorser names if present
      const userIds = [data.verified_by, data.authorized_by, data.endorsed_by].filter(Boolean);
      const userMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .in('id', userIds);
        (users || []).forEach((u: Record<string, any>) => {
          userMap[u.id] = `${u.first_name} ${u.last_name}`.trim();
        });
      }

      setViewingSubmission({
        ...data,
        operator_name: operatorName,
        verifier_name: userMap[data.verified_by] || null,
        authorizer_name: userMap[data.authorized_by] || null,
        endorser_name: userMap[data.endorsed_by] || null
      });
    } catch (err: any) {
      setViewingSubmission({ error: err.message });
    }
  }

  function closeModal() {
    setViewingSubmission(null);
  }

  function editDraft(id: string) {
    window.location.href = `/certification/submit?edit=${id}`;
  }

  async function verifySubmission(id: string, selectedUserId?: string) {
    if (!canVerify) {
      showToast('You do not have permission to verify submissions.', 'error');
      return;
    }
    const verifierId = selectedUserId || user?.id;
    if (!confirm('Verify this submission and move to authorization?')) return;

    try {
      const { error: err } = await supabase
        .from('cert_submissions')
        .update({
          submission_status: 'pending_authorization',
          verified_by: verifierId,
          verified_at: new Date().toISOString()
        })
        .eq('id', id);

      if (err) throw err;
      showToast('Submission verified successfully!', 'success');
      closeModal();
      loadSubmissions();
    } catch (err: any) {
      showToast('Error verifying submission: ' + err.message, 'error');
    }
  }

  async function authorizeSubmission(id: string, _selectedUserId?: string) {
    if (!canAuthorize) {
      showToast('You do not have permission to authorize submissions.', 'error');
      return;
    }
    if (!confirm('Authorize this submission and issue the certificate?')) return;

    try {
      // First, fetch the submission to get room_id and service_date
      const { data: submission, error: fetchErr } = await supabase
        .from('cert_submissions')
        .select('room_id, service_date')
        .eq('id', id)
        .single();

      if (fetchErr) throw fetchErr;

      // Authorise via RPC. Server enforces super-admin / global-scope role
      // and same-person guard. authorized_by is always the caller (auth.uid()).
      const { error: err } = await supabase.rpc('authorize_cert_submission', { p_submission_id: id });
      if (err) throw err;

      // Update the room's last_service_date
      if (submission.room_id) {
        const { error: roomErr } = await supabase
          .from('hosp_rooms')
          .update({ last_service_date: submission.service_date })
          .eq('id', submission.room_id);

        if (roomErr) {
          console.error('Error updating room last_service_date:', roomErr);
          // Don't fail the whole operation, just log it
        } else {
        }
      }

      showToast('Submission authorized! Certificate issued.', 'success');
      closeModal();
      loadSubmissions();
    } catch (err: any) {
      showToast('Error authorizing submission: ' + err.message, 'error');
    }
  }

  async function returnSubmission(id: string, _stage?: string) {
    const reason = prompt('Please provide a reason for returning this submission:');
    if (!reason) return;

    try {
      const { error: err } = await supabase
        .from('cert_submissions')
        .update({
          submission_status: 'returned',
          return_reason: reason,
          returned_at: new Date().toISOString(),
          returned_by: user?.id
        })
        .eq('id', id);

      if (err) throw err;
      showToast('Submission returned to operator.', 'success');
      closeModal();
      loadSubmissions();
    } catch (err: any) {
      showToast('Error returning submission: ' + err.message, 'error');
    }
  }

  function viewCertificate(id: string) {
    window.open(`/certificate.html?id=${id}`, '_blank');
  }

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Permission loading state
  if (permissionLoading) {
    return (
      <div className="placeholder-page">
        <div className="spinner" />
      </div>
    );
  }

  if (!canAccess) {
    return <AccessDenied feature="pos_reports" />;
  }

  // Loading state handled by DataTable component

  if (error) {
    return <ErrorDisplay error={error} onRetry={loadSubmissions} feature="pos_reports" />;
  }

  // Columns for DataTable
  const columns = [
    {
      key: 'invoice_number',
      label: 'Ref',
      sortable: true,
      render: (row: Record<string, any>) => <strong>{row.invoice_number || '—'}</strong>
    },
    {
      key: 'service_date',
      label: 'Date',
      sortable: true,
      render: (row: Record<string, any>) => formatDate(row.service_date)
    },
    {
      key: 'property_name',
      label: 'Property',
      sortable: true
    },
    {
      key: 'display_customer',
      label: 'Customer',
      sortable: true
    },
    {
      key: 'operator_name',
      label: 'Operator',
      sortable: true
    },
    {
      key: 'submission_status',
      label: 'Status',
      render: (row: Record<string, any>) => {
        const status = (STATUS_CONFIG as Record<string, typeof STATUS_CONFIG.draft>)[row.submission_status] || STATUS_CONFIG.draft;
        return <StatusBadge bg={status.bg} color={status.color}>{status.label}</StatusBadge>;
      }
    }
  ];

  // Filters for DataTable
  const filters = [
    { key: 'property_name', label: 'Property', options: propertyOptions },
    { key: 'submission_status', label: 'Status', options: statusOptions },
    { key: 'operator_name', label: 'Operator', options: operatorOptions }
  ];

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, padding: '12px 20px', zIndex: 1000,
          borderRadius: 'var(--radius)', fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          background: toast.type === 'error' ? 'var(--color-error-bg)' : toast.type === 'success' ? 'var(--color-success-bg)' : 'var(--color-info-bg)',
          color: toast.type === 'error' ? 'var(--color-error)' : toast.type === 'success' ? 'var(--color-success)' : 'var(--color-info)',
        }}>
          {toast.message}
        </div>
      )}

      {/* View Modal */}
      {viewingSubmission && (
        <ViewModal
          submission={viewingSubmission}
          onClose={closeModal}
          onVerify={(selectedUserId) => verifySubmission(viewingSubmission.id, selectedUserId)}
          onAuthorize={(selectedUserId) => authorizeSubmission(viewingSubmission.id, selectedUserId)}
          onReturn={(stage) => returnSubmission(viewingSubmission.id, stage)}
          onEdit={() => editDraft(viewingSubmission.id)}
          onCertificate={() => viewCertificate(viewingSubmission.id)}
          canVerify={canVerify}
          canAuthorize={canAuthorize}
          formatDate={formatDate}
          adminUsers={adminUsers}
          currentUserId={user?.id ?? ''}
        />
      )}

      {/* Property Selector */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1rem',
        padding: '1rem',
        background: 'var(--color-white)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <label style={{ fontWeight: 500, color: 'var(--color-text)' }}>
          Property:
        </label>
        <SearchSelect
          width="inline"
          options={[
            { value: '', label: '— Select a property —' },
            ...userProperties.map(p => ({ value: p.id, label: p.property_name })),
          ]}
          value={selectedPropertyId}
          onChange={setSelectedPropertyId}
        />
        {propertiesLoading && (
          <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>Loading...</span>
        )}
      </div>

      {/* List/Card View Toggle Tabs */}
      <div className="page-tabs">
        <button
          className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          📋 List View
        </button>
        <button
          className={`page-tab ${viewMode === 'card' ? 'active' : ''}`}
          onClick={() => setViewMode('card')}
        >
          🗂️ Card View
        </button>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'list' ? (
        <DataTable
          columns={columns}
          data={submissions}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search customer, ref #, operator..."
          searchKeys={['customer_name', 'display_customer', 'invoice_number', 'operator_name', 'service_location', 'property_name']}
          filters={filters}
          defaultSort={{ key: 'created_at', direction: 'desc' }}
          pageSize={50}
          actions={(row: Record<string, any>) => {
            return (
              <>
                <span className="action-icon" onClick={(e) => { e.stopPropagation(); viewSubmission(row.id); }} title="View">👁️</span>
                {row.submission_status === 'draft' && (
                  <span className="action-icon" onClick={(e) => { e.stopPropagation(); editDraft(row.id); }} title="Edit">✏️</span>
                )}
                {row.submission_status === 'pending_verification' && canVerify && (
                  <span className="action-icon action-icon--success" onClick={(e) => { e.stopPropagation(); verifySubmission(row.id); }} title="Verify">✓</span>
                )}
                {row.submission_status === 'pending_authorization' && canAuthorizeRow(row.verified_by) && (
                  <span className="action-icon action-icon--success" onClick={(e) => { e.stopPropagation(); authorizeSubmission(row.id); }} title="Authorise">✓</span>
                )}
                {row.submission_status === 'certified' && (
                  <span className="action-icon" onClick={(e) => { e.stopPropagation(); viewCertificate(row.id); }} title="View Certificate">📜</span>
                )}
              </>
            );
          }}
          onRowClick={(row: Record<string, any>) => viewSubmission(row.id)}
          emptyMessage="No POS submissions found"
        />
      ) : (
        <BoardView
          submissionsByStatus={submissionsByStatus}
          onView={viewSubmission}
          onEdit={editDraft}
          onVerify={verifySubmission}
          onAuthorize={authorizeSubmission}
          canVerify={canVerify}
          canAuthorizeRow={canAuthorizeRow}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

// Kanban Board View Component
interface BoardViewProps {
  submissionsByStatus: Record<string, Record<string, any>[]>;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onVerify: (id: string) => void;
  onAuthorize: (id: string) => void;
  canVerify: boolean;
  canAuthorizeRow: (verifiedBy: string | null | undefined) => boolean;
  formatDate: (dateStr: string | null | undefined) => string;
}

function BoardView({ submissionsByStatus, onView, onEdit, onVerify, onAuthorize, canVerify, canAuthorizeRow, formatDate }: BoardViewProps) {
  return (
    <div className="ops-board">
      {WORKFLOW_COLUMNS.map(col => {
        const subs = submissionsByStatus[col.id] || [];
        const status = (STATUS_CONFIG as Record<string, typeof STATUS_CONFIG.draft>)[col.id] || STATUS_CONFIG.draft;

        return (
          <div key={col.id} className="ops-board-column">
            <div className="ops-board-column-header" style={{ borderTopColor: status.color }}>
              <span className="ops-board-column-title">{col.label}</span>
              <span className="ops-board-column-count">{subs.length}</span>
            </div>
            <div className="ops-board-column-body">
              {subs.length === 0 ? (
                <div className="ops-board-empty">No items</div>
              ) : (
                subs.slice(0, 20).map(sub => (
                  <div key={sub.id} className="ops-board-card" onClick={() => onView(sub.id)}>
                    <div className="ops-board-card-header">
                      <span className="ops-board-card-customer">{sub.display_customer}</span>
                    </div>
                    <div className="ops-board-card-meta">
                      <span>{formatDate(sub.service_date)}</span>
                      {sub.invoice_number && (
                        <span className="ops-split-chip-sm">{sub.invoice_number}</span>
                      )}
                    </div>
                    <div className="ops-board-card-meta">
                      <span>By: {sub.operator_name}</span>
                    </div>
                    <div className="ops-board-card-actions" onClick={e => e.stopPropagation()}>
                      {sub.submission_status === 'draft' && (
                        <button className="ops-board-action" onClick={() => onEdit(sub.id)}>Edit</button>
                      )}
                      {sub.submission_status === 'pending_verification' && canVerify && (
                        <button className="ops-board-action ops-board-action-primary" onClick={() => onVerify(sub.id)}>
                          Verify
                        </button>
                      )}
                      {sub.submission_status === 'pending_authorization' && canAuthorizeRow(sub.verified_by) && (
                        <button className="ops-board-action ops-board-action-primary" onClick={() => onAuthorize(sub.id)}>
                          Authorize
                        </button>
                      )}
                      {sub.submission_status === 'returned' && (
                        <button className="ops-board-action" onClick={() => onEdit(sub.id)}>Resubmit</button>
                      )}
                    </div>
                  </div>
                ))
              )}
              {subs.length > 20 && (
                <div className="ops-board-more">+{subs.length - 20} more</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// View Modal - Full version matching Certification.jsx
interface AdminUser {
  id: string;
  name: string;
  canVerify: boolean;
  canAuthorize: boolean;
}

interface ViewModalProps {
  submission: Record<string, any>;
  onClose: () => void;
  onVerify: (selectedUserId: string) => void;
  onAuthorize: (selectedUserId: string) => void;
  onReturn: (stage: string) => void;
  onEdit: () => void;
  onCertificate: () => void;
  canVerify: boolean;
  canAuthorize: boolean;
  formatDate: (dateStr: string | null | undefined) => string;
  adminUsers?: AdminUser[];
  currentUserId: string;
}

function ViewModal({ submission, onClose, onVerify, onAuthorize, onReturn, onEdit, onCertificate, canVerify, canAuthorize, formatDate, adminUsers = [], currentUserId }: ViewModalProps) {
  // State for selected verifier/authorizer - defaults to current user
  const [selectedVerifierId, setSelectedVerifierId] = useState(currentUserId);
  const [selectedAuthorizerId, setSelectedAuthorizerId] = useState(currentUserId);

  // Filter admin users by capability
  const verifierOptions = adminUsers.filter(u => u.canVerify);
  const authorizerOptions = adminUsers.filter(u => u.canAuthorize);

  if (submission.loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
          <div className="placeholder-page">
            <div className="spinner" />
            <p>Loading submission...</p>
          </div>
        </div>
      </div>
    );
  }

  if (submission.error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
          <div className="empty-state">
            <p>Error: {submission.error}</p>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const sub = submission;
  const status = (STATUS_CONFIG as Record<string, typeof STATUS_CONFIG.draft>)[sub.submission_status] || STATUS_CONFIG.draft;

  const showVerifyActions = sub.submission_status === 'pending_verification';
  const showAuthorizeActions = sub.submission_status === 'pending_authorization';
  const showEditAction = sub.submission_status === 'draft' || sub.submission_status === 'returned';
  const showCertificateAction = sub.submission_status === 'certified';

  // Parse customer name and address from service_location
  let customerName = sub.customer_name || '—';
  let serviceAddress = '—';
  if (sub.service_location) {
    const parts = sub.service_location.split(' - ');
    if (!sub.customer_name) {
      customerName = parts[0] || '—';
    }
    serviceAddress = parts.slice(1).join(' - ') || '—';
  }

  // Print via hidden iframe — avoids popup-window restrictions in standalone
  // PWAs (where window.open + .close() can strand the user on a blank tab).
  const printSubmission = () => {
    const modalContent = document.getElementById('cert-modal-content')?.innerHTML || '';
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:-10000px;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    if (!doc) {
      iframe.remove();
      return;
    }

    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>Certification Submission</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 40px; color: #333; }
        h2, h3, h4 { color: #722cbb; margin-top: 20px; }
        section { margin-bottom: 24px; page-break-inside: avoid; }
        .status-badge { display: inline-block; padding: 4px 12px; border-radius: 4px; }
        @media print { body { padding: 20px; } button { display: none !important; } }
      </style>
      </head><body>
      <h1 style="color: #722cbb; border-bottom: 3px solid #722cbb; padding-bottom: 10px;">CleanMyBed Certification Submission</h1>
      ${modalContent}
      </body></html>`);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => iframe.remove(), 100);
    }, 100);
  };

  const exportSubmissionPDF = () => {
    alert('Use your browser\'s print dialog and select "Save as PDF" as the destination.');
    printSubmission();
  };

  // Group equipment by type
  const groupedEquipment: Record<string, Record<string, any>[]> = {};
  if (sub.equipment_used && Array.isArray(sub.equipment_used)) {
    sub.equipment_used.forEach((eq: Record<string, any>) => {
      const type = eq.equipment_type || 'Other';
      if (!groupedEquipment[type]) groupedEquipment[type] = [];
      groupedEquipment[type].push(eq);
    });
  }

  // Parse items
  const parseItems = (description: string | null | undefined): string[] => {
    if (!description) return [];
    return description.split(';').map((i: string) => i.trim()).filter(Boolean);
  };

  const primaryItems = parseItems(sub.primary_asset_description);
  const additionalItems = parseItems(sub.additional_items_description);
  const sundries = sub.sundries && Array.isArray(sub.sundries) ? sub.sundries : [];

  // Checklist step titles
  const stepTitles: Record<string, string> = {
    step3: 'HEPA Extraction Quality',
    step4: 'UVC Treatment Quality',
    step5: 'Dry & Chemical-Free Assurance',
    step6: 'Safety & Containment',
    step7: 'Service Notes',
    step8: 'Final Declaration',
    step3_pre_service_setup: 'Pre-Service Setup (Legacy)',
    step4_vacuum_equipment: 'Vacuum Equipment Check (Legacy)',
    step5_hepa_extraction: 'HEPA Extraction (Legacy)',
    step6_uvc_equipment: 'UVC Equipment Check (Legacy)',
    step7_uvc_treatment: 'UVC Treatment (Legacy)',
    step8_compliance_safety: 'Compliance & Safety (Legacy)',
    step9_service_notes: 'Service Notes (Legacy)',
    step10_operator_confirmation: 'Operator Confirmation (Legacy)'
  };

  const questionLabels: Record<string, Record<string, string>> = {
    step3: { q1: 'HEPA extraction completed', q2: 'Overlapping passes used', q3: 'All surfaces extracted' },
    step4: { q1: 'UVC treatment completed', q2: 'Correct duration/dosage', q3: 'Safety protocols followed' },
    step5: { q1: 'No moisture used', q2: 'No chemicals used' },
    step6: { q1: 'Material disposed correctly', q2: 'Appropriate PPE worn', q3: 'Room left clean and safe' },
    step7: { service_notes: 'Service Notes' },
    step8: { protocol_compliance: 'Protocol Compliance', signature_name: 'Signature', signed_at: 'Signed At' }
  };

  const stepOrder = [
    'step3', 'step4', 'step5', 'step6', 'step7', 'step8',
    'step3_pre_service_setup', 'step4_vacuum_equipment', 'step5_hepa_extraction',
    'step6_uvc_equipment', 'step7_uvc_treatment', 'step8_compliance_safety',
    'step9_service_notes', 'step10_operator_confirmation'
  ];

  const checklist = sub.checklist_data || {};

  // Format checklist value
  const formatChecklistValue = (value: unknown): ReactNode => {
    if (value === 'yes' || value === true || value === 'true') {
      return <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>✓ Yes</span>;
    }
    if (value === 'no' || value === false || value === 'false') {
      return <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>✗ No</span>;
    }
    return String(value ?? '');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <h2>Submission Details</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={printSubmission} className="btn btn-secondary btn-sm">
              Print
            </button>
            <button onClick={exportSubmissionPDF} className="btn btn-secondary btn-sm">
              Export PDF
            </button>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {/* Modal Body */}
        <div id="cert-modal-content" className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gap: '24px' }}>

            {/* Service Information */}
            <div className="view-section">
              <div className="section-title">Service Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><strong>Reference:</strong> {sub.invoice_number || '—'}</div>
                <div><strong>Date:</strong> {formatDate(sub.service_date)}</div>
                <div><strong>Customer Name:</strong> {customerName}</div>
                <div><strong>Property:</strong> {sub.property_name || '—'}</div>
                <div><strong>Service Location:</strong> {serviceAddress}</div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong>Status:</strong>{' '}
                  <StatusBadge bg={status.bg} color={status.color}>{status.label}</StatusBadge>
                </div>
              </div>
            </div>

            {/* Operator Information */}
            <div className="view-section">
              <div className="section-title">Performed By</div>
              <div>
                <strong>Operator:</strong> {sub.operator_name}{' '}
                <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">✓ ACCREDITED</StatusBadge>
              </div>
            </div>

            {/* Equipment Used */}
            {Object.keys(groupedEquipment).length > 0 && (
              <div className="view-section">
                <div className="section-title">Equipment Used</div>
                {Object.entries(groupedEquipment).map(([type, items]) => (
                  <div key={type} style={{ marginBottom: '16px' }}>
                    <strong className="text-primary">
                      {type}: <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">✓ CERTIFIED</StatusBadge>
                    </strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {items.map((eq, idx) => (
                        <li key={idx} style={{ marginBottom: '4px' }}>
                          {eq.model || 'Unknown Model'} ({eq.serial_number || '—'})
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Items Serviced */}
            {(primaryItems.length > 0 || additionalItems.length > 0 || sundries.length > 0) && (
              <div className="view-section">
                <div className="section-title">Items Serviced</div>

                {primaryItems.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong className="text-primary">Primary Assets (Beds):</strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {primaryItems.map((item, idx) => {
                        const match = item.match(/(.+?),\s*(.+)/);
                        if (match && match[1] && match[2]) {
                          return <li key={idx} style={{ marginBottom: '4px' }}>{match[1].trim()} ({match[2].trim()})</li>;
                        }
                        return <li key={idx} style={{ marginBottom: '4px' }}>{item}</li>;
                      })}
                    </ul>
                  </div>
                )}

                {additionalItems.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong className="text-primary">Additional Items:</strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {additionalItems.map((item, idx) => {
                        const match = item.match(/(.+?),\s*(.+)/);
                        if (match && match[1] && match[2]) {
                          return <li key={idx} style={{ marginBottom: '4px' }}>{match[1].trim()} ({match[2].trim()})</li>;
                        }
                        return <li key={idx} style={{ marginBottom: '4px' }}>{item}</li>;
                      })}
                    </ul>
                  </div>
                )}

                {sundries.length > 0 && (
                  <div>
                    <strong className="text-primary">Other Items:</strong>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px', marginTop: '4px' }}>
                      {sundries.map((sundry, idx) => (
                        <li key={idx} style={{ marginBottom: '4px' }}>
                          {sundry.item_type || 'Unknown Item'} ({sundry.quantity || 1})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Checklist Responses */}
            {Object.keys(checklist).length > 0 && (
              <div className="view-section">
                <div className="section-title">Checklist Responses</div>
                {stepOrder.map(stepKey => {
                  const stepData = checklist[stepKey];
                  if (!stepData) return null;

                  return (
                    <div key={stepKey} style={{ marginBottom: '20px' }}>
                      <h4 style={{ color: 'var(--color-text)', marginBottom: '10px' }}>{stepTitles[stepKey] || stepKey}</h4>
                      <div style={{ display: 'grid', gap: '8px', paddingLeft: '16px' }}>
                        {Object.keys(stepData).map(key => {
                          if (key === 'general_comments' && stepData[key]) {
                            return <div key={key}><strong>General Comments:</strong> {stepData[key]}</div>;
                          }
                          if (!key.endsWith('_comment') && stepData[key] !== null && stepData[key] !== undefined) {
                            const label = (questionLabels[stepKey] && questionLabels[stepKey][key])
                              || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            const value = stepData[key];
                            const commentKey = key + '_comment';
                            const comment = stepData[commentKey];

                            return (
                              <div key={key}>
                                <strong>{label}:</strong> {formatChecklistValue(value)}
                                {comment && <div style={{ color: 'var(--color-text-light)', marginLeft: '20px', fontStyle: 'italic' }}>{comment}</div>}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Workflow History */}
            <div className="view-section" style={{ borderBottom: 'none' }}>
              <div className="section-title">Workflow History</div>
              <div style={{ display: 'grid', gap: '16px' }}>

                {/* Submitted Step */}
                {sub.created_at && (
                  <div style={{ padding: '12px', background: 'var(--color-bg-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">✓ Submitted</StatusBadge>
                    </div>
                    <strong>By:</strong> {sub.operator_name}<br />
                    <strong>Date:</strong> {formatDate(sub.created_at)}
                  </div>
                )}

                {/* Verified Step */}
                {sub.verified_by && (
                  <div style={{ padding: '12px', background: 'var(--color-bg-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">✓ Verified</StatusBadge>
                    </div>
                    <strong>By:</strong> {sub.verifier_name}<br />
                    <strong>Date:</strong> {formatDate(sub.verified_at)}
                    {sub.verification_notes && <><br /><strong>Notes:</strong> {sub.verification_notes}</>}
                  </div>
                )}

                {/* Authorized Step */}
                {sub.authorized_by && (
                  <div style={{ padding: '12px', background: 'var(--color-bg-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">✓ Authorised</StatusBadge>
                    </div>
                    <strong>By:</strong> {sub.authorizer_name}<br />
                    <strong>Date:</strong> {formatDate(sub.authorized_at)}
                    {sub.authorization_notes && <><br /><strong>Notes:</strong> {sub.authorization_notes}</>}
                  </div>
                )}

                {/* Endorsed Step */}
                {sub.endorsed_by && (
                  <div style={{ padding: '12px', background: 'var(--color-bg-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <StatusBadge bg="rgba(16, 185, 129, 0.1)" color="var(--color-success)">✓ Endorsed</StatusBadge>
                    </div>
                    <strong>By:</strong> {sub.endorser_name}<br />
                    <strong>Date:</strong> {formatDate(sub.endorsed_at)}
                    {sub.endorsement_notes && <><br /><strong>Notes:</strong> {sub.endorsement_notes}</>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>

          {showEditAction && (
            <button className="btn btn-primary" onClick={onEdit}>
              {sub.submission_status === 'returned' ? 'Edit & Resubmit' : 'Edit'}
            </button>
          )}

          {showVerifyActions && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto' }}>
                <label style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>Verified by:</label>
                <SearchSelect
                  width="inline"
                  options={verifierOptions.map(u => ({ value: u.id, label: u.name }))}
                  value={selectedVerifierId}
                  onChange={setSelectedVerifierId}
                />
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => onReturn('verification')}
                disabled={!canVerify}
                style={!canVerify ? { opacity: 0.5 } : {}}
              >
                Return
              </button>
              <button
                className="btn btn-primary"
                onClick={() => onVerify(selectedVerifierId)}
                disabled={!canVerify}
                style={!canVerify ? { opacity: 0.5 } : {}}
              >
                Verify
              </button>
            </>
          )}

          {showAuthorizeActions && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto' }}>
                <label style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>Authorised by:</label>
                <SearchSelect
                  width="inline"
                  options={authorizerOptions.map(u => ({ value: u.id, label: u.name }))}
                  value={selectedAuthorizerId}
                  onChange={setSelectedAuthorizerId}
                />
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => onReturn('authorization')}
                disabled={!canAuthorize}
                style={!canAuthorize ? { opacity: 0.5 } : {}}
              >
                Return
              </button>
              <button
                className="btn btn-primary"
                onClick={() => onAuthorize(selectedAuthorizerId)}
                disabled={!canAuthorize}
                style={!canAuthorize ? { opacity: 0.5 } : {}}
              >
                Authorise & Issue
              </button>
            </>
          )}

          {showCertificateAction && (
            <button className="btn btn-primary" onClick={onCertificate}>
              View Certificate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default POSCertificates;
